import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { CopyCycleDialog } from "@/components/CopyCycleDialog";
import { Plus, Pencil, Trash2, BookOpen, Users, CalendarPlus, FileText } from "lucide-react";
import { docenteLabel } from "@/lib/format";

const GRADOS_POR_NIVEL = {
  PREESCOLAR: [1, 2, 3],
  PRIMARIA: [1, 2, 3, 4, 5, 6],
  SECUNDARIA: [1, 2, 3],
};

const empty = { plantel_id: "", grado: 1, letra: "A", ciclo: "2026-2027", docente_id: "__none__" };

export default function Grupos() {
  const { user } = useAuth();
  const isDocente = user?.role === "docente";
  const [grupos, setGrupos] = useState([]);
  const [planteles, setPlanteles] = useState([]);
  const [docentes, setDocentes] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterPlantel, setFilterPlantel] = useState("__all__");
  const [copyOpen, setCopyOpen] = useState(false);

  const load = async () => {
    try {
      const requests = [
        api.get("/grupos", { params: filterPlantel !== "__all__" ? { plantel_id: filterPlantel } : {} }),
        api.get("/planteles"),
      ];
      // Non-docente roles need the docente list to assign titulares.
      // Superadmin uses /docentes (global); other roles use /usuarios?role=docente (scoped).
      if (!isDocente) {
        const isSuperadmin = user?.role === "superadmin";
        const req = isSuperadmin
          ? api.get("/docentes")
          : api.get("/usuarios", { params: { role: "docente" } });
        requests.push(req.catch(() => ({ data: [] })));
      }
      const [g, p, d] = await Promise.all(requests);
      // Solo Primaria activa por ahora
      const plantelesPrim = (p.data || []).filter((pl) => pl.nivel === "PRIMARIA");
      const plantelIds = new Set(plantelesPrim.map((pl) => pl.id));
      const gruposPrim = (g.data || []).filter((gr) => plantelIds.has(gr.plantel_id));
      setGrupos(gruposPrim);
      setPlanteles(plantelesPrim);
      setDocentes(d?.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [filterPlantel]);

  const currentPlantel = planteles.find((p) => p.id === form.plantel_id);
  const gradosDisponibles = currentPlantel ? GRADOS_POR_NIVEL[currentPlantel.nivel] : [];

  const startCreate = () => {
    setEditing(null);
    setForm({ ...empty, plantel_id: planteles[0]?.id || "" });
    setOpen(true);
  };

  const startEdit = (g) => {
    setEditing(g);
    setForm({
      plantel_id: g.plantel_id,
      grado: g.grado,
      letra: g.letra,
      ciclo: g.ciclo,
      docente_id: g.docente_id || "__none__",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, docente_id: form.docente_id === "__none__" ? null : form.docente_id };
      if (isDocente) payload.docente_id = user.id; // always self
      if (editing) {
        const { plantel_id, ...upd } = payload; // no cambiar plantel al editar
        await api.put(`/grupos/${editing.id}`, upd);
        toast.success("Grupo actualizado");
      } else {
        await api.post("/grupos", payload);
        toast.success("Grupo creado");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/grupos/${confirm.id}`);
      toast.success("Grupo eliminado");
      setConfirm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppLayout>
      <div className="max-w-7xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">
              {isDocente ? "Docente" : "Administración"}
            </div>
            <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {isDocente ? "Mis grupos" : "Grupos escolares"}
            </h1>
            {isDocente && (
              <p className="text-sm text-[#545454] mt-1">
                Cree, edite o elimine sus grupos dentro de los planteles asignados por su director.
              </p>
            )}
          </div>
          <div className="flex gap-2 items-center">
            <div className="w-64">
              <Select value={filterPlantel} onValueChange={setFilterPlantel}>
                <SelectTrigger data-testid="filter-plantel"><SelectValue placeholder="Filtrar por plantel" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los planteles</SelectItem>
                  {planteles.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!isDocente && (
              <Button
                data-testid="btn-add-grupo"
                onClick={startCreate}
                disabled={planteles.length === 0}
                className="bg-[#235B4E] hover:bg-[#1D4A40] text-white"
              >
                <Plus className="w-4 h-4 mr-1" /> Nuevo grupo
              </Button>
            )}
          </div>
        </div>

        {planteles.length === 0 && (
          <div className="mb-4 p-4 border border-[#BC955C]/40 bg-[#BC955C]/10 rounded text-sm text-[#374151]">
            {isDocente
              ? "Aún no tiene planteles asignados. Contacte al director para que le asigne al menos uno."
              : "Debe registrar al menos un plantel antes de crear grupos."}
          </div>
        )}

        <div className="bg-white border border-[#E5E7EB] rounded overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F9FAFB]">
                <TableHead>Plantel</TableHead>
                <TableHead>Nivel</TableHead>
                <TableHead>Grado</TableHead>
                <TableHead>Grupo</TableHead>
                {!isDocente && <TableHead>Docente titular</TableHead>}
                <TableHead>Alumnos</TableHead>
                <TableHead>Ciclo</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {grupos.length === 0 ? (
                <TableRow><TableCell colSpan={isDocente ? 7 : 8} className="text-center py-10">
                  <BookOpen className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
                  <div className="text-sm text-[#545454]">Sin grupos registrados</div>
                </TableCell></TableRow>
              ) : grupos.map((g) => (
                <TableRow key={g.id} data-testid={`grupo-row-${g.id}`}>
                  <TableCell className="font-medium">{g.plantel?.nombre || "—"}</TableCell>
                  <TableCell>
                    <span className="text-xs px-2 py-0.5 rounded bg-[#235B4E]/10 text-[#235B4E]">
                      {g.plantel?.nivel}
                    </span>
                  </TableCell>
                  <TableCell>{g.grado}°</TableCell>
                  <TableCell className="font-semibold">{g.letra}</TableCell>
                  {!isDocente && (
                    <TableCell>{g.docente ? docenteLabel(g.docente) : <span className="text-[#9CA3AF]">Sin asignar</span>}</TableCell>
                  )}
                  <TableCell>
                    <Link
                      data-testid={`link-alumnos-${g.id}`}
                      to={`/grupos/${g.id}/alumnos`}
                      className="inline-flex items-center gap-1 text-[#235B4E] hover:underline"
                    >
                      <Users className="w-3.5 h-3.5" /> {g.alumnos_count}
                    </Link>
                  </TableCell>
                  <TableCell className="text-xs">{g.ciclo}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      data-testid={`btn-datos-est-${g.id}`}
                      variant="ghost" size="sm"
                      onClick={() => window.open(`/grupos/${g.id}/datos-estadisticos`, "_blank")}
                      title="Datos estadísticos del grupo"
                    >
                      <FileText className="w-4 h-4 text-[#235B4E]" />
                    </Button>
                    <Button data-testid={`btn-edit-g-${g.id}`} variant="ghost" size="sm" onClick={() => startEdit(g)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button data-testid={`btn-delete-g-${g.id}`} variant="ghost" size="sm" onClick={() => setConfirm(g)}>
                      <Trash2 className="w-4 h-4 text-[#9F2241]" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar grupo" : "Nuevo grupo"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Plantel *</Label>
              <Select
                value={form.plantel_id}
                onValueChange={(v) => setForm({ ...form, plantel_id: v, grado: 1 })}
                disabled={!!editing}
              >
                <SelectTrigger data-testid="grupo-plantel"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
                <SelectContent>
                  {planteles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.nombre} ({p.nivel})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Grado *</Label>
                <Select value={String(form.grado)} onValueChange={(v) => setForm({ ...form, grado: parseInt(v, 10) })}>
                  <SelectTrigger data-testid="grupo-grado"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {gradosDisponibles.map((g) => <SelectItem key={g} value={String(g)}>{g}°</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Letra (A, B, C…) *</Label>
                <Input data-testid="grupo-letra" required value={form.letra}
                       onChange={(e) => setForm({ ...form, letra: e.target.value.toUpperCase() })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Ciclo escolar</Label>
              <Input value={form.ciclo} onChange={(e) => setForm({ ...form, ciclo: e.target.value })} />
            </div>
            {!isDocente && (
              <div className="space-y-1.5">
                <Label className="text-xs">Docente titular</Label>
                <Select value={form.docente_id || "__none__"} onValueChange={(v) => setForm({ ...form, docente_id: v })}>
                  <SelectTrigger data-testid="grupo-docente"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin asignar</SelectItem>
                    {docentes.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter className="mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="btn-save-g" type="submit" disabled={saving} className="bg-[#235B4E] hover:bg-[#1D4A40]">
                {saving ? "Guardando…" : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar grupo?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán también todos los alumnos de este grupo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[#9F2241] hover:bg-[#801C34]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CopyCycleDialog
        open={copyOpen}
        onOpenChange={setCopyOpen}
        currentCiclo={grupos[0]?.ciclo}
        grupos={grupos}
        onDone={load}
      />
    </AppLayout>
  );
}
